import { useState, useEffect, useCallback } from 'react';

interface UseScrollTriggerProps {
  threshold?: number;
  disableHysteresis?: boolean;
}

export function useScrollTrigger({
  threshold = 100,
  disableHysteresis = false,
}: UseScrollTriggerProps = {}) {
  const [trigger, setTrigger] = useState(false);

  const handleScroll = useCallback(() => {
    const { scrollY } = window;
    const { scrollHeight, clientHeight } = document.documentElement;

    // Check if we're near the bottom of the page (within 100px)
    const isNearBottom = scrollHeight - scrollY - clientHeight < 100;

    if (disableHysteresis) {
      // Simple threshold-based trigger, but don't trigger if near bottom
      setTrigger(scrollY > threshold && !isNearBottom);
    } else {
      // Direction-based logic with improved stability
      const direction = scrollY - (handleScroll as any).previous || 0;

      // Only trigger if we're scrolling down significantly and past threshold
      if (direction > 5 && scrollY > threshold && !isNearBottom) {
        setTrigger(true);
      }
      // Show header when scrolling up or near bottom
      else if (direction < -5 || isNearBottom || scrollY <= threshold) {
        setTrigger(false);
      }
    }

    (handleScroll as any).previous = scrollY;
  }, [threshold, disableHysteresis]);

  useEffect(() => {
    // Initialize previous scroll position
    (handleScroll as any).previous = window.scrollY;

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return trigger;
}
